import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Suspense, useCallback, useState } from 'react';
import clsx from 'clsx';
import Parser, { domToReact } from 'html-react-parser';
import { useRouter } from '@tribeplatform/react-sdk';
import { useAuthToken, useSpaceUtil } from '@tribeplatform/react-sdk/hooks';
import { getDomainInfo } from '../common';
import { init, lowlight, ReactLowlight } from '../common/lib/lowlight';
import { MediaModal } from '../MediaModal';
import { ComposerModuleName } from './@types';
import { AttachmentRenderer } from './AttachmentRenderer';
import { CodeCopyButton } from './CodeCopyButton';
import { EmbedRenderer } from './EmbedRenderer';
import { renderAudio, renderImg, renderVideo } from './renderers';
import { imgToImage } from './utils';
init();
export const HtmlContent = ({ value, embeds, attachments, mentions, context, trustedDomains = [], }) => {
    const { generateMemberPath } = useSpaceUtil();
    const { Link } = useRouter();
    const { data: { networkPublicInfo }, } = useAuthToken();
    const { domain, subfolder } = getDomainInfo(networkPublicInfo);
    if ((trustedDomains === null || trustedDomains === void 0 ? void 0 : trustedDomains.indexOf(networkPublicInfo === null || networkPublicInfo === void 0 ? void 0 : networkPublicInfo.domain)) === -1) {
        trustedDomains.push(networkPublicInfo === null || networkPublicInfo === void 0 ? void 0 : networkPublicInfo.domain);
    }
    if (subfolder && (trustedDomains === null || trustedDomains === void 0 ? void 0 : trustedDomains.indexOf(domain)) === -1) {
        trustedDomains.push(domain);
    }
    let mediaIndex = -1;
    // The first one is active by default.
    const [activeMediaIndex, setActiveMediaIndex] = useState(0);
    const [slides, setSlides] = useState([]);
    const [mediaModalVisibility, setMediaModalVisibility] = useState(false);
    // Keeping embedClassName for future usages
    const embedClassName = '';
    const handleModalVisibility = useCallback(e => {
        var _a;
        setActiveMediaIndex(Number((_a = e === null || e === void 0 ? void 0 : e.currentTarget.dataset) === null || _a === void 0 ? void 0 : _a.index));
        setMediaModalVisibility(true);
    }, []);
    const handleModalClose = useCallback(() => {
        setMediaModalVisibility(false);
    }, []);
    if (!value) {
        return null;
    }
    const pushSlide = (newSlide) => {
        setSlides(slides => [...slides, newSlide]);
    };
    const options = {
        replace: _domNode => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
            const domNode = _domNode;
            const tagName = domNode.name;
            let isTrusted;
            switch (tagName) {
                case 'figure': {
                    switch ((_a = domNode.firstChild) === null || _a === void 0 ? void 0 : _a.name) {
                        case 'img': {
                            const imgSlide = imgToImage(domNode.firstChild);
                            if (imgSlide) {
                                slides.push(imgSlide);
                            }
                            mediaIndex += 1;
                            return renderImg(domNode.firstChild, {
                                className: clsx('rounded-md overflow-hidden', embedClassName),
                                mediaIndex,
                                onClick: handleModalVisibility,
                                context,
                            });
                        }
                        case 'audio':
                            return renderAudio(domNode.firstChild);
                        case 'video':
                            return renderVideo(domNode.firstChild, {
                                className: embedClassName,
                            });
                        default:
                            return domNode;
                    }
                }
                case 'img': {
                    const imgSlide = imgToImage(domNode);
                    if (imgSlide) {
                        slides.push(imgSlide);
                    }
                    mediaIndex += 1;
                    return renderImg(domNode, {
                        className: clsx('rounded-md overflow-hidden', embedClassName),
                        mediaIndex,
                        onClick: handleModalVisibility,
                        context,
                    });
                }
                case 'h1':
                case 'h2':
                    return (_jsx("h2", { className: "text-xl", children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                case 'audio':
                    return renderAudio(domNode);
                case 'video':
                    return renderVideo(domNode, { className: embedClassName });
                case 'code':
                    return (_jsx("code", { className: "bg-surface-pressed text-content rounded px-1.5 py-0.5", children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                case 'pre': {
                    if (!((_b = domNode === null || domNode === void 0 ? void 0 : domNode.children) === null || _b === void 0 ? void 0 : _b.length) ||
                        ((_c = domNode === null || domNode === void 0 ? void 0 : domNode.children[0]) === null || _c === void 0 ? void 0 : _c.name) !== 'code' ||
                        ((_e = (_d = domNode === null || domNode === void 0 ? void 0 : domNode.children[0]) === null || _d === void 0 ? void 0 : _d.children) === null || _e === void 0 ? void 0 : _e.length) !== 1 ||
                        ((_g = (_f = domNode === null || domNode === void 0 ? void 0 : domNode.children[0]) === null || _f === void 0 ? void 0 : _f.children[0]) === null || _g === void 0 ? void 0 : _g.type) !== 'text') {
                        return null;
                    }
                    let codeLanguage = null;
                    if ((_j = (_h = domNode === null || domNode === void 0 ? void 0 : domNode.children[0]) === null || _h === void 0 ? void 0 : _h.attribs) === null || _j === void 0 ? void 0 : _j.class) {
                        const detectedLanguage = (_l = (_k = domNode === null || domNode === void 0 ? void 0 : domNode.children[0]) === null || _k === void 0 ? void 0 : _k.attribs) === null || _l === void 0 ? void 0 : _l.class.replace('language-', '');
                        codeLanguage = lowlight.listLanguages().includes(detectedLanguage)
                            ? detectedLanguage
                            : null;
                    }
                    const content = domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children[0].children);
                    return (_jsxs("div", { className: clsx('code-wrapper relative', embedClassName), children: [_jsx(CodeCopyButton, { content: content }, void 0), typeof window !== 'undefined' ? (_jsx(Suspense, { fallback: _jsx("pre", { children: _jsx("code", { children: content }, void 0) }, void 0), children: _jsx(ReactLowlight, { language: codeLanguage, value: content }, void 0) }, void 0)) : (_jsx("pre", { children: _jsx("code", { children: content }, void 0) }, void 0))] }, void 0));
                }
                case 'h3':
                    return (_jsx("h2", { className: "text-lg", children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                case 'ol':
                    return (_jsx("ol", { start: (_m = domNode === null || domNode === void 0 ? void 0 : domNode.attribs) === null || _m === void 0 ? void 0 : _m.start, className: "list-decimal list-inside ps-6", children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                case 'ul':
                    return (_jsx("ul", { className: "list-disc list-inside ps-6", children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                case 'div':
                    if (domNode.type === 'tag' &&
                        ((_o = domNode.attribs) === null || _o === void 0 ? void 0 : _o['data-type']) === ComposerModuleName.Embed) {
                        return (_jsx(EmbedRenderer, { domNode: domNode, embeds: embeds, className: embedClassName, context: context }, void 0));
                    }
                    break;
                case 'attachment':
                    if (domNode.type === 'tag' &&
                        ((_p = domNode.attribs) === null || _p === void 0 ? void 0 : _p['data-type']) === ComposerModuleName.Attachment) {
                        return (_jsx(AttachmentRenderer, { domNode: domNode, attachments: attachments, slides: slides, pushSlide: pushSlide, onClick: handleModalVisibility }, void 0));
                    }
                    break;
                case 'a':
                    if (((_q = domNode.attribs) === null || _q === void 0 ? void 0 : _q['data-type']) === ComposerModuleName.Mention) {
                        const mentionId = domNode.attribs['data-id'];
                        const mentionFromDb = (_r = mentions === null || mentions === void 0 ? void 0 : mentions.find) === null || _r === void 0 ? void 0 : _r.call(mentions, mention => mention.id === mentionId);
                        let str = (mentionFromDb === null || mentionFromDb === void 0 ? void 0 : mentionFromDb.name)
                            ? `${mentionFromDb.name}`
                            : (_t = (_s = domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options)) === null || _s === void 0 ? void 0 : _s.toString) === null || _t === void 0 ? void 0 : _t.call(_s);
                        if ((str === null || str === void 0 ? void 0 : str[0]) === '@') {
                            str = str.replace('@', '');
                        }
                        return (_jsx(Link, { variant: "accent", href: generateMemberPath({ id: mentionId }), trusted: true, children: str }, void 0));
                    }
                    isTrusted = !!(trustedDomains === null || trustedDomains === void 0 ? void 0 : trustedDomains.find(trustedDomain => {
                        var _a, _b;
                        return trustedDomain === '*' ||
                            ((_b = (_a = domNode === null || domNode === void 0 ? void 0 : domNode.attribs) === null || _a === void 0 ? void 0 : _a.href) === null || _b === void 0 ? void 0 : _b.indexOf(`https://${trustedDomain}`)) === 0;
                    }));
                    return (_jsx(Link, { variant: "accent", href: (_u = domNode === null || domNode === void 0 ? void 0 : domNode.attribs) === null || _u === void 0 ? void 0 : _u.href, 
                        // If isTrusted is false, let the Link component decide based on isInternal
                        trusted: isTrusted || undefined, children: domToReact(domNode === null || domNode === void 0 ? void 0 : domNode.children, options) }, void 0));
                case 'iframe':
                case 'script':
                    return _jsx(_Fragment, {}, void 0);
                default:
                    // If null is returned parser will render the html variant.
                    return null;
            }
            return null;
        },
    };
    const Component = Parser(value, options);
    return (_jsxs(_Fragment, { children: [Component, (slides === null || slides === void 0 ? void 0 : slides.length) > 0 && (_jsx(MediaModal, { slides: slides, isVisible: mediaModalVisibility, onClose: handleModalClose, activeMediaIndex: activeMediaIndex }, void 0))] }, void 0));
};
//# sourceMappingURL=HtmlContent.js.map